<template>
  <div
    v-if="status == 'success' && data?.data"
    :class="`single collection-${collection}`"
  >
    <!-- INTRO -->
    <slot
      v-if="data.data.attributes.intro"
      name="intro"
      :intro="data.data.attributes.intro"
      :item="data.data"
    >
      <Component
        :is="introComponent"
        v-bind="{ ...data.data.attributes.intro, idx: 0 }"
      />
    </slot>
    <slot :item="data?.data" :refresh="refresh" :error="error">
      <component :is="contentWrapper">
        <slot name="prepend" :item="data.data" />
        <component
          :is="blocksWrapper"
          v-if="data.data.attributes.blocks"
          id="fk-blocks"
        >
          <FkStrapiBlock
            v-for="(block, idx) in data.data.attributes.blocks"
            :key="`${block.id}-${block.__component}`"
            :wrapper="blockWrapper"
            :block="{ ...block, idx }"
          />
        </component>
        <slot name="append" :item="data.data" />
      </component>
    </slot>
  </div>
</template>

<script setup lang="ts" generic="T extends Strapi4ResponseData<StrapiPage>">
import {
  useSeoMeta,
  useHead,
  useRuntimeConfig,
  watch,
  provide,
  watchEffect,
  ref,
  useRoute,
  useJsonld,
  useI18n,
  useSwitchLocalePath,
  useStrapi,
  useAsyncData,
  useSetI18nParams,
  useLocalePath,
  showError,
  computed,
  useNuxtApp,
  createError,
  onUnmounted,
} from "#imports";
import type { ConcreteComponent } from "vue";
import { useCollectionConfig } from "../../composables/useCollectionConfig";
import type {
  Strapi4RequestParams,
  StrapiLocale,
  Strapi4ResponseData,
} from "@nuxtjs/strapi";
import type { StrapiPage } from "../../../../src/types";
import { useInjectionKeys } from "../../composables/useInjectionKeys";
import { useParentSlugFilter } from "../../composables/useParentSlugFilter";
import type { BreadcrumbLink } from "#ui/types";

const props = withDefaults(
  defineProps<{
    modelValue?: T;
    slug?: string | string[];
    wrapper?: ConcreteComponent | string;
    contentWrapper?: ConcreteComponent | string;
    blocksWrapper?: ConcreteComponent | string;
    blockWrapper?: ConcreteComponent | string;
    collection?: string;
    filterByParent?: boolean;
    setSeoMeta?: boolean;
    fetchBreadcrumbs?: boolean;
  }>(),
  {
    modelValue: undefined,
    slug: undefined,
    collection: "page",
    wrapper: () => "div",
    contentWrapper: () => "div",
    blocksWrapper: () => "div",
    blockWrapper: () => "div",
    filterByParent: true,
    setSeoMeta: true,
    fetchBreadcrumbs: true,
  },
);
const emit = defineEmits<{
  (e: "update:modelValue", item?: T): void;
}>();
const { getCollectionConfig, pageCollection, seoField } = useCollectionConfig();
const route = useRoute();
const collection = ref(props.collection ?? pageCollection);
const { introComponent, populate, singular } = getCollectionConfig(
  `${collection.value}`,
);
const { differentDomains, locale } = useI18n();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();
const setI18nParams = useSetI18nParams();
const { findOne } = useStrapi();

// helper function to build the localized slug
const getLocalizedSlug = (attributes: StrapiPage) => {
  let slug = attributes.slug;
  //if the slug === home, replace it with an empty string
  if (slug === "home") slug = "";
  // if the parent has a slug, prepend it to the slug
  let parent = attributes.parent;
  while (parent && parent.data && parent.data.attributes) {
    slug = `${parent.data.attributes.slug}/${slug}`;
    parent = parent.data.attributes.parent;
  }

  return slug;
};
const createLocalizedSlugs = (attributes: StrapiPage) => {
  const slugs: { [key: string]: { slug: string | undefined } } = {};
  if (attributes) {
    slugs[attributes.locale] = {
      slug: getLocalizedSlug(attributes),
    };

    const attrs = attributes;
    if (
      attrs.localizations &&
      attrs.localizations.data &&
      Array.isArray(attrs.localizations.data)
    ) {
      attrs.localizations.data.forEach((l) => {
        slugs[l.attributes.locale] = {
          slug: getLocalizedSlug(l.attributes),
        };
      });
    }
  }

  return slugs;
};
const createQueryParams = (
  slug: string | string[],
  useParent = false,
  filters: Record<string, unknown> = {},
  customPopulate?: Record<string, unknown>,
) => {
  // helper function to populate the parents
  const createParentPopulate = (idx: number): object => {
    if (idx === slug.length - 1) return {};
    return {
      parent: {
        fields,
        populate: createParentPopulate(idx + 1),
      },
    };
  };

  // the localization fields to fetch
  const fields = ["slug", "title", "locale"];
  // populate fields
  const toPopulate: Record<string, unknown> | undefined =
    customPopulate ?? populate ?? {};
  toPopulate.localizations = {
    fields,
    populate: useParent ? createParentPopulate(0) : {},
  };
  toPopulate.parent = {};
  if (useParent) {
    toPopulate.parent = {
      fields,
      populate: createParentPopulate(0),
    };
  }

  // create the params for the request
  const params: Strapi4RequestParams = {
    locale: locale.value as StrapiLocale,
    populate,
    filters,
  };

  return params;
};
const createBreadcumbs = async (attributes: StrapiPage) => {
  if (!attributes || attributes.slug === "home") return [];
  const breadcrumbs: BreadcrumbLink[] = [];
  breadcrumbs.push({
    label: `${attributes.title}`,
    to: localePath({
      name: collection.value == "page" ? `slug` : `${collection.value}-slug`,
      params: {
        slug: getLocalizedSlug(attributes)?.split("/"),
      },
    }),
  });

  let parent = attributes.parent;
  const path = route.path;
  if (collection.value !== pageCollection && path) {
    const parentSlugs = path.split("/");
    parentSlugs.pop();
    if (path.startsWith("/")) parentSlugs.shift();
    ///TODO: Respect settings from i18n in nuxt.config (prefixes, etc.)
    if (parentSlugs.length > 0 && locale.value === parentSlugs[0]) {
      parentSlugs.shift();
    }
    const parentSlug = parentSlugs.pop();

    if (parentSlug) {
      // get parent data. We assume that only the first element in the array of slugs is a page, the rest is a collection
      const pageConfig = getCollectionConfig(pageCollection);
      const params = createQueryParams(
        parentSlug,
        true,
        {},
        pageConfig.populate,
      );
      const { data: parentData } = await useAsyncData(
        `${locale.value}/single/${pageCollection}/${parentSlug}`,
        () =>
          findOne<StrapiPage>(
            `slugify/slugs/${pageConfig.singular}`,
            parentSlug,
            params,
          ),
        {
          immediate: true,
        },
      );
      if (parentData.value) {
        parent = parentData.value;
      }
    }
  }

  while (parent && parent.data && parent.data.attributes) {
    breadcrumbs.push({
      label: parent.data.attributes.title,
      to: localePath({
        name: `slug`,
        params: {
          slug: getLocalizedSlug(parent.data.attributes)?.split("/"),
        },
      }),
    });
    parent = parent.data.attributes.parent;
  }

  breadcrumbs.push({
    label: "Home",
    to: localePath("/"),
  });

  breadcrumbs.reverse();
  return breadcrumbs;
};

//get the slug
const slugs = ref(["home"]);
if (props.slug) {
  slugs.value = Array.isArray(props.slug) ? props.slug : [props.slug];
} else if (route.params.slug && route.params.slug !== "") {
  slugs.value = Array.isArray(route.params.slug)
    ? route.params.slug
    : [route.params.slug];
}
// parent filter when slugs is array
const useParent = collection.value == "page" || props.filterByParent;
const filters = useParent ? useParentSlugFilter(slugs.value) : {};
const params = computed(() => {
  return createQueryParams(slugs.value, useParent, filters);
});
const nuxtApp = useNuxtApp();
const { data, status, error, refresh } = await useAsyncData(
  `${locale.value}/single/${collection.value}/${slugs.value.join("/")}`,
  () =>
    findOne<StrapiPage>(
      `slugify/slugs/${singular}`,
      slugs.value[slugs.value.length - 1],
      params.value,
    ).catch((e) => {
      throw createError(e.error);
    }),
  {
    watch: [slugs],
    immediate: true,
    getCachedData: (key) =>
      nuxtApp.$config.public.flyingkiwi.enableCache
        ? nuxtApp.payload.data[key] || nuxtApp.static.data[key]
        : nuxtApp.isHydrating
          ? nuxtApp.payload.data[key]
          : nuxtApp.static.data[key],
  },
);
const errorWatchStopHandle = watch(
  error,
  () => {
    if (error.value) {
      showError({
        statusCode: error.value.statusCode,
        message: error.value.message,
      });
    }
  },
  {
    immediate: true,
  },
);
const dataWatchStopHandle = watch(
  data,
  () => {
    if (data.value) {
      const i18nSlugs = createLocalizedSlugs(data.value.data.attributes);
      setI18nParams(i18nSlugs);
    }
    emit("update:modelValue", data.value?.data as T);
  },
  { immediate: true },
);

const { itemKey, collectionKey, breadcrumbsKey } = useInjectionKeys();
provide(itemKey, data.value?.data);
provide(collectionKey, collection.value ?? "");

provide(
  breadcrumbsKey,
  props.fetchBreadcrumbs && data.value
    ? await createBreadcumbs(data.value.data.attributes)
    : [],
);

const config = useRuntimeConfig();

watchEffect(() => {
  if (props.setSeoMeta) {
    // Page title
    let pageName = data.value?.data?.attributes.title ?? "";
    if (pageName.toLowerCase() === "home") {
      pageName = "";
    }
    let pageTitle = pageName + " - " + config.public.flyingkiwi.siteName;
    if (pageTitle.startsWith(" - ") || pageTitle.startsWith(" | ")) {
      pageTitle = pageTitle.substring(3);
    }

    let metaTitle = pageTitle;
    let canonical; // = config.public.flyingkiwi.domain + route.path;

    // SEO data
    //TODO: Better merge the old and new Strapi-component data so we only have to use useSeoData once.
    const seo = data.value?.data?.attributes[`${seoField}`];
    if (seo) {
      /// check for old or new seo-strapi-components by checking for fields on the seo-object
      if (
        Object.hasOwn(seo, "metaTitle") ||
        Object.hasOwn(seo, "metaDescription") ||
        Object.hasOwn(seo, "metaImage")
      ) {
        //find fist paragraph in intro.text
        let description = data.value?.data?.attributes.intro?.text ?? "";
        let image =
          data.value?.data?.attributes.intro?.image?.data?.attributes.url ?? "";

        const ogTitle = seo.metaTitle ?? pageTitle;
        description = seo.metaDescription ?? description;
        image = seo.metaImage?.data?.attributes.url ?? image;

        useSeoMeta({
          title: ogTitle,
          ogTitle: ogTitle,
          description: `${description}`,
          ogDescription: `${description}`,
          ogImage: image,
        });

        useHead(() => {
          return {
            meta: [
              {
                name: "title",
                content: ogTitle,
              },
            ],
          };
        });
      } else {
        //Assume new seo-component
        let description =
          seo.metaData?.metaDescription ?? seo.openGraph?.ogDescription ?? "";

        //Check for emtpy description, then grab the first paragraph from the intro.text which contains a Array of JSON block objects
        if (description === "") {
          const introText = data.value?.data?.attributes.intro?.text;
          if (introText) {
            if (Array.isArray(introText)) {
              const firstParagraph = introText.find(
                (block: { type: string }) => block.type === "paragraph",
              );
              if (firstParagraph && firstParagraph.children) {
                description = firstParagraph.children
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((child: any) => {
                    return child.text;
                  })
                  .join(" ");
              }
            } else {
              description = introText;
            }
          }
        }

        const keywords = seo.metaData?.keywords ?? "";
        const robots = seo.metaData?.robots;

        // Remove stripe when the title is not set
        if (pageTitle.startsWith(" - ") || pageTitle.startsWith(" | ")) {
          pageTitle = pageTitle.substring(3);
        }

        // Open Graph data
        const ogTitle =
          seo.openGraph?.ogTitle ?? seo.metaData?.metaTitle ?? pageTitle ?? "";
        const ogDescription =
          seo.openGraph?.ogDescription ?? seo.metaData?.metaDescription ?? "";

        const ogImage =
          seo.openGraph?.ogImage?.data?.attributes?.url ??
          data.value?.data?.attributes.intro?.image?.data?.attributes?.url ??
          "";

        const ogType = seo.openGraph?.ogType;
        const ogCompany = config.public.flyingkiwi.siteName;

        // URL of the current location
        const ogURL = differentDomains
          ? switchLocalePath(locale.value)
          : config.public.flyingkiwi.domain + route.path;
        // canonical = ogURL;
        if (seo.metaData?.canonicalsURL) {
          canonical = seo.metaData?.canonicalsURL;
        }

        // Removing html from the descriptions
        const cleanDescription = description?.replace(/<[^>]*>/g, "");
        const cleanogDescription = ogDescription?.replace(/<[^>]*>/g, "");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const input: any = {
          title: ogTitle,
          description: cleanDescription,
          keywords: keywords,
          viewport: "width=device-width, initial-scale=1.0",

          ogTitle: ogTitle,
          ogDescription: cleanogDescription,
          ogImage: ogImage,
          ogType: ogType,
          ogSiteName: ogCompany,
          ogUrl: ogURL,
        };
        if (robots) {
          input.robots = robots;
        }
        useSeoMeta(input);

        // Structured Data
        const markup = seo.structuredData?.structuredData ?? null;

        useJsonld(markup);

        // Google result title
        metaTitle = seo.metaData?.metaTitle ?? ogTitle ?? pageTitle ?? "";
      }
    } else {
      let description = "";
      let image = "";
      if (data.value?.data?.attributes.intro) {
        const introText = data.value?.data?.attributes.intro?.text;
        if (introText) {
          if (Array.isArray(introText)) {
            const firstParagraph = introText.find(
              (block: { type: string }) => block.type === "paragraph",
            );
            if (firstParagraph && firstParagraph.children) {
              description = firstParagraph.children
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((child: any) => {
                  return child.text;
                })
                .join(" ");
            }
          } else {
            description = introText;
          }
        }
        image =
          data.value?.data?.attributes.intro.image?.data?.attributes.url ?? "";
      }

      useSeoMeta({
        title: metaTitle,
        ogTitle: metaTitle,
        description: `${description}`,
        ogDescription: `${description}`,
        ogImage: image,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const links: any = [];
    if (canonical) {
      links.push({
        rel: "canonical",
        href: canonical,
      });
    }

    useHead(() => {
      return {
        meta: [
          {
            name: "title",
            content: metaTitle,
          },
        ],
        link: links,
      };
    });
  }
});

onUnmounted(() => {
  errorWatchStopHandle();
  dataWatchStopHandle();
});
</script>
